import { useEffect, useMemo, useRef, useState } from 'react';
import AskBox, { ChatMessages } from './AskBox';
import ConciergeHome from './ConciergeHome';
import ConciergeLauncher from './ConciergeLauncher';
import { useClient } from '../context/ClientContext';

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

const WINDOW_EDGE_MARGIN = 20;
// Reserve space below the panel for the persistent bubble launcher (bottom-5
// = 20px + ~60px button + 8px gap).
const WINDOW_BOTTOM_MARGIN = 88;

function ConciergeWindowSession({
  clientId,
  initialQuery,
  initialAnswerId,
  preferredScenarioCode,
  windowMode,
  isOpen,
  onHasMessagesChange,
}) {
  const { clientConfig } = useClient();
  const scrollContainerRef = useRef(null);
  const chat = AskBox({
    clientId,
    personaId: null,
    initialQuery,
    initialAnswerId,
    scrollContainerRef,
    preferredScenarioCode,
    surfaceMode: windowMode,
  });

  useEffect(() => {
    onHasMessagesChange?.(chat.messages.length > 0);
  }, [chat.messages.length, onHasMessagesChange]);

  // Focus input only when window transitions from closed to open
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const timer = setTimeout(() => chat.focusInput(), 100);
      wasOpenRef.current = true;
      return () => clearTimeout(timer);
    }
    if (!isOpen) {
      wasOpenRef.current = false;
    }
  }, [isOpen, chat.focusInput]);

  return (
    <>
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto bg-stone-50 px-4 sm:px-5">
        {chat.messages.length === 0 && chat.isSeededLaunchPending && !chat.authInline ? (
          <div className="flex h-full min-h-[220px] items-center justify-center py-8">
            <div className="rounded-2xl border border-slate-200 bg-white/90 px-5 py-4 text-center shadow-sm shadow-slate-200/40">
              <div className="mx-auto flex w-fit items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full bg-slate-300 animate-bounce-custom"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="h-2 w-2 rounded-full bg-slate-300 animate-bounce-custom"
                  style={{ animationDelay: '150ms' }}
                />
                <span
                  className="h-2 w-2 rounded-full bg-slate-300 animate-bounce-custom"
                  style={{ animationDelay: '300ms' }}
                />
              </div>
              <p className="mt-3 text-sm font-medium text-slate-700">
                {chat.isAuthSubmitting ? 'Opening member context...' : 'Preparing your question...'}
              </p>
            </div>
          </div>
        ) : chat.messages.length === 0 && chat.authInline ? (
          <div className="min-h-[64px]" />
        ) : chat.messages.length === 0 ? (
          <ConciergeHome clientId={clientId} chat={chat} mode={windowMode} showDevPanel={false} />
        ) : (
          <div className="py-5">
            <ChatMessages
              messages={chat.messages}
              surfaceMode={windowMode}
              messagesEndRef={chat.messagesEndRef}
              isLoading={chat.isLoading}
              statusMessage={chat.statusMessage}
              currentStage={chat.currentStage}
              currentTool={chat.currentTool}
              currentToolQuery={chat.currentToolQuery}
              currentToolTargets={chat.currentToolTargets}
              sessionId={chat.sessionId}
              clientId={chat.clientId}
              inlineFollowupQuestion={chat.inlineFollowupQuestion}
              inlineFollowupMessageIndex={chat.inlineFollowupMessageIndex}
              isInlineFollowupSubmitting={chat.isInlineFollowupSubmitting}
              onInlineFollowupAnswer={chat.onInlineFollowupAnswer}
              onInlineFollowupSkip={chat.onInlineFollowupSkip}
              onRetrySystemError={chat.onRetrySystemError}
              authPrompt={chat.authPrompt}
              authMessageIndex={chat.authMessageIndex}
              authFormOpen={chat.authFormOpen}
              authStep={chat.authStep}
              authEmail={chat.authEmail}
              authCode={chat.authCode}
              authManualCodeOpen={chat.authManualCodeOpen}
              authScenarioOptions={chat.authScenarioOptions}
              preferredScenarioCode={chat.preferredScenarioCode}
              authError={chat.authError}
              authSessionTtlDays={chat.authSessionTtlDays}
              isAuthSubmitting={chat.isAuthSubmitting}
              hideInstantAnswerChip={chat.hideInstantAnswerChip}
              onNextActionClick={chat.onNextActionClick}
              onHandoverFormSubmit={chat.onHandoverFormSubmit}
              onAuthEmailChange={chat.onAuthEmailChange}
              onAuthCodeChange={chat.onAuthCodeChange}
              onAuthStart={chat.onAuthStart}
              onAuthVerify={chat.onAuthVerify}
              onAuthSelectScenario={chat.onAuthSelectScenario}
              onAuthToggleManualCode={chat.onAuthToggleManualCode}
              onAuthOpenInline={chat.onAuthOpenInline}
              onAuthResetToEmail={chat.onAuthResetToEmail}
            />
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 bg-white/98 px-4 py-3 backdrop-blur sm:px-5">
        {chat.input}
        {chat.messages.length === 0 && (!chat.isSeededLaunchPending || chat.authInline) && (
          <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
            {chat.windowAuthBar}
            {chat.authInline && <div className="flex justify-center">{chat.authInline}</div>}
          </div>
        )}
        {chat.messages.length > 0 && chat.conversationAuthInline && (
          <div className="mt-3 flex justify-center">{chat.conversationAuthInline}</div>
        )}
        {chat.messages.length > 0 && (
          <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-2.5">
            <div className="min-w-0 flex-1">{chat.windowConversationBar}</div>
            <button
              type="button"
              onClick={chat.clearConversation}
              className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-600 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
            >
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-slate-100 text-[12px] leading-none text-slate-500">
                +
              </span>
              <span>New chat</span>
            </button>
          </div>
        )}
        {chat.newConversationConfirm}
      </div>
    </>
  );
}

export default function ConciergeWindow({
  clientId,
  isOpen,
  onOpen,
  onClose,
  ctaLabel = 'Chat with Concierge',
  initialQuery = '',
  initialAnswerId = null,
  preferredScenarioCode = null,
  windowKey = 'default',
  launcherVariant = 'bubble',
}) {
  const { clientConfig } = useClient();
  const [hasOpenedOnce, setHasOpenedOnce] = useState(isOpen);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 640 : false,
  );
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1440,
    height: typeof window !== 'undefined' ? window.innerHeight : 960,
  }));
  const [isExpanded, setIsExpanded] = useState(false);
  const [position, setPosition] = useState(null);
  const [hasMessages, setHasMessages] = useState(false);
  const dragStateRef = useRef(null);

  const panelWidth = isMobile
    ? viewportSize.width
    : Math.min(viewportSize.width - 40, isExpanded ? 980 : 620);
  const panelHeight = isMobile
    ? viewportSize.height
    : Math.min(viewportSize.height - 32, isExpanded ? 900 : 780);

  useEffect(() => {
    if (isOpen) {
      setHasOpenedOnce(true);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleResize() {
      const nextViewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      setViewportSize(nextViewport);
      setIsMobile(nextViewport.width < 640);
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setPosition(null);
      return;
    }

    const nextDefault = {
      x: viewportSize.width - panelWidth - WINDOW_EDGE_MARGIN,
      y: Math.max(WINDOW_EDGE_MARGIN, viewportSize.height - panelHeight - WINDOW_BOTTOM_MARGIN),
    };

    setPosition((current) => {
      if (!current) {
        return nextDefault;
      }
      return {
        x: clamp(
          current.x,
          WINDOW_EDGE_MARGIN,
          Math.max(WINDOW_EDGE_MARGIN, viewportSize.width - panelWidth - WINDOW_EDGE_MARGIN),
        ),
        y: clamp(
          current.y,
          WINDOW_EDGE_MARGIN,
          Math.max(WINDOW_EDGE_MARGIN, viewportSize.height - panelHeight - WINDOW_BOTTOM_MARGIN),
        ),
      };
    });
  }, [isMobile, panelWidth, panelHeight, viewportSize.height, viewportSize.width]);

  useEffect(() => {
    return () => {
      if (dragStateRef.current?.cleanup) {
        dragStateRef.current.cleanup();
      }
    };
  }, []);

  const windowMode = isMobile ? 'mobile' : isExpanded ? 'expanded' : 'compact';
  const panelStyle = isMobile
    ? undefined
    : {
        width: `${panelWidth}px`,
        height: `${panelHeight}px`,
        left: `${position?.x ?? 0}px`,
        top: `${position?.y ?? 0}px`,
      };
  const title = clientConfig?.conciergeName || 'Conci';
  const subtitle = clientConfig?.orgName || clientId;

  const handleToggleExpanded = () => {
    if (isMobile) return;

    const nextExpanded = !isExpanded;
    const nextWidth = Math.min(viewportSize.width - 40, nextExpanded ? 980 : 620);
    const nextHeight = Math.min(viewportSize.height - 32, nextExpanded ? 900 : 780);

    setPosition((current) => {
      const currentPosition = current || {
        x: viewportSize.width - panelWidth - WINDOW_EDGE_MARGIN,
        y: viewportSize.height - panelHeight - WINDOW_BOTTOM_MARGIN,
      };
      const rightOffset = viewportSize.width - (currentPosition.x + panelWidth);
      const bottomOffset = viewportSize.height - (currentPosition.y + panelHeight);

      return {
        x: clamp(
          viewportSize.width - nextWidth - Math.max(WINDOW_EDGE_MARGIN, rightOffset),
          WINDOW_EDGE_MARGIN,
          Math.max(WINDOW_EDGE_MARGIN, viewportSize.width - nextWidth - WINDOW_EDGE_MARGIN),
        ),
        y: clamp(
          viewportSize.height - nextHeight - Math.max(WINDOW_BOTTOM_MARGIN, bottomOffset),
          WINDOW_EDGE_MARGIN,
          Math.max(WINDOW_EDGE_MARGIN, viewportSize.height - nextHeight - WINDOW_BOTTOM_MARGIN),
        ),
      };
    });
    setIsExpanded(nextExpanded);
  };

  const startDrag = (event) => {
    if (isMobile) return;
    event.preventDefault();

    const startX = event.clientX;
    const startY = event.clientY;
    const startPosition = position || { x: 0, y: 0 };

    const handleMove = (moveEvent) => {
      const nextX = startPosition.x + (moveEvent.clientX - startX);
      const nextY = startPosition.y + (moveEvent.clientY - startY);
      setPosition({
        x: clamp(
          nextX,
          WINDOW_EDGE_MARGIN,
          Math.max(WINDOW_EDGE_MARGIN, viewportSize.width - panelWidth - WINDOW_EDGE_MARGIN),
        ),
        y: clamp(
          nextY,
          WINDOW_EDGE_MARGIN,
          Math.max(WINDOW_EDGE_MARGIN, viewportSize.height - panelHeight - WINDOW_BOTTOM_MARGIN),
        ),
      });
    };

    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      dragStateRef.current = null;
    };

    dragStateRef.current = {
      cleanup: handleUp,
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  return (
    <>
      {(!isOpen || !isMobile) && (
        <ConciergeLauncher
          onClick={isOpen ? onClose : onOpen}
          label={ctaLabel}
          hasUnread={hasMessages}
          variant={launcherVariant}
          isOpen={isOpen && !isMobile}
        />
      )}

      {isOpen &&
        (isMobile ? (
          <button
            type="button"
            aria-label="Close chat"
            onClick={onClose}
            className="fixed inset-0 z-40 bg-slate-950/25 backdrop-blur-[1px]"
          />
        ) : (
          <button
            type="button"
            aria-label="Close chat"
            onClick={onClose}
            className="fixed inset-0 z-40 bg-slate-950/8"
          />
        ))}

      {hasOpenedOnce && (
        <div
          className={`fixed z-50 flex flex-col overflow-hidden border border-slate-200 bg-white shadow-2xl shadow-slate-950/15 transition-all duration-200 ${
            isMobile ? 'inset-0 rounded-none' : 'rounded-[14px]'
          } ${
            isOpen
              ? 'pointer-events-auto opacity-100'
              : 'pointer-events-none translate-y-4 opacity-0'
          }`}
          style={panelStyle}
        >
          <div
            className={`flex items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-5 ${isMobile ? '' : 'cursor-move'}`}
            onMouseDown={startDrag}
          >
            <div className="min-w-0">
              <p className="truncate text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                ThoughtFabric Concierge
              </p>
              <div className="truncate text-sm font-medium text-slate-900">
                {title}
                <span className="ml-2 text-slate-400">·</span>
                <span className="ml-2 font-normal text-slate-500">{subtitle}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isMobile && (
                <button
                  type="button"
                  onClick={handleToggleExpanded}
                  onMouseDown={(event) => event.stopPropagation()}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
                  aria-label={isExpanded ? 'Collapse chat window' : 'Expand chat window'}
                >
                  {isExpanded ? (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.8"
                        d="M8 4H4v4m0-4 6 6m6-6h4v4m0-4-6 6M8 20H4v-4m0 4 6-6m6 6h4v-4m0 4-6-6"
                      />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.8"
                        d="M15 3h6v6m0-6-7 7M9 21H3v-6m0 6 7-7"
                      />
                    </svg>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                onMouseDown={(event) => event.stopPropagation()}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
                aria-label="Close chat"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.8"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <ConciergeWindowSession
            key={windowKey}
            clientId={clientId}
            initialQuery={initialQuery}
            initialAnswerId={initialAnswerId}
            preferredScenarioCode={preferredScenarioCode}
            windowMode={windowMode}
            isOpen={isOpen}
            onHasMessagesChange={setHasMessages}
          />
        </div>
      )}
    </>
  );
}
